<template>
  <div :style="`transition: opacity 0.5s ease-in;opacity: ${isLoadMapStyleFiles ? 1 : 0};`">
    <div v-if="!hasBmView" ref="view"
         style="width: 100%; height: 100%">
    </div>
    <slot></slot>
  </div>
</template>

<script>
import events from 'vue-baidu-map/components/base/events.js'
import bindEvents from 'vue-baidu-map/components/base/bindEvent.js'
import { checkType } from 'vue-baidu-map/components/base/util.js'

/**
 * JavaScript API GL：https://lbsyun.baidu.com/index.php?title=jspopularGL
 * 百度地图JSAPI WebGL v1.0类参考：https://mapopen-pub-jsapi.bj.bcebos.com/jsapi/reference/jsapi_webgl_1_0.html
 * 个性化地图绘制：https://lbsyun.baidu.com/apiconsole/custommap
 * Vue Baidu Map：https://dafrok.github.io/vue-baidu-map
 * 基于Vue Baidu Map旧版修改为WebGL版本
 */
export default {
  name: 'BMapGL',
  props: {
    ak: {
      type: String,
    },
    proxy: {
      type: String,
    },
    /**
     * https://lbs.baidu.com/index.php?title=jspopularGL/guide/custom#通过样式ID调用个性化地图样式（推荐）
     */
    styleId: {
      type: String,
    },
    /**
     * https://lbs.baidu.com/index.php?title=jspopularGL/guide/custom#通过样式JSON调用个性化地图样式
     */
    styleConfig: {
      type: Array,
    },
    center: {
      type: [Object, String],
    },
    zoom: {
      type: Number,
    },
    minZoom: {
      type: Number,
    },
    maxZoom: {
      type: Number,
    },
    highResolution: {
      type: Boolean,
      default: true,
    },
    mapClick: {
      type: Boolean,
      default: true,
    },
    mapType: {
      type: String,
    },
    dragging: {
      type: Boolean,
      default: true,
    },
    scrollWheelZoom: {
      type: Boolean,
      default: false,
    },
    doubleClickZoom: {
      type: Boolean,
      default: true,
    },
    keyboard: {
      type: Boolean,
      default: true,
    },
    inertialDragging: {
      type: Boolean,
      default: true,
    },
    continuousZoom: {
      type: Boolean,
      default: true,
    },
    pinchToZoom: {
      type: Boolean,
      default: true,
    },
    autoResize: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    center (val, oldVal) {
      const {
        map,
        zoom,
      } = this
      if (checkType(val) === 'String' && val !== oldVal) {
        map.centerAndZoom(val, zoom)
      }
    },
    'center.lng' (val, oldVal) {
      const {
        BMap,
        map,
        zoom,
        center,
      } = this
      if (val !== oldVal && val >= -180 && val <= 180) {
        map.centerAndZoom(new BMap.Point(val, center.lat), zoom)
      }
    },
    'center.lat' (val, oldVal) {
      const {
        BMap,
        map,
        zoom,
        center,
      } = this
      if (val !== oldVal && val >= -74 && val <= 74) {
        map.centerAndZoom(new BMap.Point(center.lng, val), zoom)
      }
    },
    zoom (val, oldVal) {
      const { map } = this
      if (val !== oldVal && val >= 3 && val <= 19) {
        map.setZoom(val)
      }
    },
    minZoom (val) {
      const { map } = this
      map.setMinZoom(val)
    },
    maxZoom (val) {
      const { map } = this
      map.setMaxZoom(val)
    },
    highResolution () {
      this.reset()
    },
    mapClick () {
      this.reset()
    },
    mapType (val) {
      const { map } = this
      map.setMapType(global[val])
    },
    dragging (val) {
      const { map } = this
      val ? map.enableDragging() : map.disableDragging()
    },
    scrollWheelZoom (val) {
      const { map } = this
      val ? map.enableScrollWheelZoom() : map.disableScrollWheelZoom()
    },
    doubleClickZoom (val) {
      const { map } = this
      val ? map.enableDoubleClickZoom() : map.disableDoubleClickZoom()
    },
    keyboard (val) {
      const { map } = this
      val ? map.enableKeyboard() : map.disableKeyboard()
    },
    inertialDragging (val) {
      const { map } = this
      val ? map.enableInertialDragging() : map.disableInertialDragging()
    },
    continuousZoom (val) {
      const { map } = this
      val ? map.enableContinuousZoom() : map.disableContinuousZoom()
    },
    pinchToZoom (val) {
      const { map } = this
      val ? map.enablePinchToZoom() : map.disablePinchToZoom()
    },
    autoResize (val) {
      const { map } = this
      val ? map.enableAutoResize() : map.disableAutoResize()
    },
    styleId () {
      const {
        map,
        styleId,
      } = this
      styleId && map.setMapStyleV2({ styleId })
    },
    styleConfig () {
      const {
        map,
        styleConfig,
      } = this
      styleConfig && map.setMapStyleV2(styleConfig)
    },
  },
  methods: {
    setMapOptions () {
      const {
        map,
        minZoom,
        maxZoom,
        mapType,
        dragging,
        scrollWheelZoom,
        doubleClickZoom,
        keyboard,
        inertialDragging,
        continuousZoom,
        pinchToZoom,
        autoResize,
      } = this
      minZoom && map.setMinZoom(minZoom)
      maxZoom && map.setMaxZoom(maxZoom)
      mapType && map.setMapType(global[mapType])
      dragging ? map.enableDragging() : map.disableDragging()
      scrollWheelZoom ? map.enableScrollWheelZoom() : map.disableScrollWheelZoom()
      doubleClickZoom ? map.enableDoubleClickZoom() : map.disableDoubleClickZoom()
      keyboard ? map.enableKeyboard() : map.disableKeyboard()
      inertialDragging ? map.enableInertialDragging() : map.disableInertialDragging()
      continuousZoom ? map.enableContinuousZoom() : map.disableContinuousZoom()
      pinchToZoom ? map.enablePinchToZoom() : map.disablePinchToZoom()
      autoResize ? map.enableAutoResize() : map.disableAutoResize()
    },
    init (BMap) {
      if (this.map) {
        return
      }
      let $el = this.$refs.view
      for (const $node of this.$slots.default || []) {
        if ($node.componentOptions && $node.componentOptions.tag === 'bm-view') {
          this.hasBmView = true
          $el = $node.elm
        }
      }
      const map = new BMap.Map($el, {
        enableHighResolution: this.highResolution,
        enableMapClick: this.mapClick,
      })
      this.map = map
      const {
        setMapOptions,
        zoom,
        getCenterPoint,
        styleId,
        styleConfig,
      } = this
      setMapOptions()
      bindEvents.call(this, map, events['bm-map'])
      // 此处强行初始化一次地图 回避一个由于错误的 center 字符串导致初始化失败抛出的错误
      map.reset()
      map.centerAndZoom(getCenterPoint(), zoom)
      // setMapStyleV2方法需要在地图初始化（centerAndZoom）完成后执行；
      styleConfig ? map.setMapStyleV2(styleConfig) : (styleId && map.setMapStyleV2({ styleId }))
      // 如果有样式，则需要等待加载，否则会白屏，
      // https://blog.csdn.net/Ysmooth_Alone/article/details/108725205
      if (styleConfig || styleId) {
        // 加载地图当前样式所需要的样式文件，callback为加载成功后的回调函数
        // 未知原因，loadMapStyleFiles可能出现不回调，但增加延迟可以解决。
        setTimeout(() => {
          // map.loadMapStyleFiles(() => {
          this.isLoadMapStyleFiles = true
          // })
        }, 300)
      } else {
        this.isLoadMapStyleFiles = true
      }
      this.$emit('ready', {
        BMap,
        map,
      })
      // Debug
      // global.map = map
      // global.mapComponent = this
    },
    getCenterPoint () {
      const {
        center,
        BMap,
      } = this
      switch (checkType(center)) {
        case 'String':
          return center
        case 'Object':
          return new BMap.Point(center.lng, center.lat)
        default:
          return new BMap.Point()
      }
    },
    initMap (BMap) {
      this.BMap = BMap
      this.init(BMap)
    },
    getMapScript () {
      if (!global.BMap) {
        global.BMap = {}
        // eslint-disable-next-line no-async-promise-executor
        global.BMap._preloader = new Promise(async (resolve) => {
          global.BMapInit = () => {
            debugger
            global.BMap = global.BMapGL
            resolve(global.BMapGL)
          }
          /**
           * JS API支持ak代理隐藏开发者侧配置
           * https://lbs.baidu.com/index.php?title=jspopularGL/guide/getkey#service-page-anchor3
           */
          if (this.proxy) {
            const proxy = this.proxy
            if (!window._BMapSecurityConfig) {
              window._BMapSecurityConfig = {}
            }
            window._BMapSecurityConfig.serviceHost = proxy
            // await $util.loadScript(`${proxy}api?type=webgl&v=1.0&callback=BMapInit`)
            // await loadScript(`${proxy}api?v=3.0&callback=BMapInit`)
          } else {
            // const ak = this.ak || this._BMap().ak
            // await $util.loadScript(`//api.map.baidu.com/api?type=webgl&v=1.0&ak=${ak}&callback=BMapInit`)
            // await loadScript(`//api.map.baidu.com/api?v=3.0&ak=${ak}&callback=BMapInit`)
          }
        })
        return global.BMap._preloader
      } else if (!global.BMap._preloader) {
        return Promise.resolve(global.BMap)
      } else {
        return global.BMap._preloader
      }
    },
    reset () {
      const {
        getMapScript,
        initMap,
      } = this
      getMapScript()
        .then(initMap)
    },
  },
  mounted () {
    this.reset()
  },
  data () {
    return {
      isLoadMapStyleFiles: false,
      hasBmView: false,
    }
  },
}
</script>



// WEBPACK FOOTER //
// src/components/BMapGL/map/BMapGL.vue